import { useEffect, useMemo, useState } from "react";
import Map from "@components/Mapa/Map";
import StickyModalFooter from "@components/General/StickyModalFooter";
import { Application } from "@core/Application";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { Modal } from "@shared/designSystem/messages/Modal";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { TextInput } from "@shared/designSystem/TextInput";
import { SmallText } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { CoordinatesFields, validateLatitude, validateLongitude } from "./CoordinatesFields";
import { CoordinatesHelpVideo } from "./CoordinatesHelpVideo";

// Same limit as the backend (QontactPointCreation.maxTriggerDistance)
const maxTriggerDistanceInMeters = 10000;

interface NewQontactPointModalProps {
    isOpen: boolean;
    orgID: string;
    onClose: () => void;
    /** Called after the qontact point was created (e.g. to refresh the list) */
    onCreated: () => void;
}

const emptyErrors = { name: "", latitude: "", longitude: "", triggerDistance: "" };

/**
 * "Nuevo Qontact Point" modal (design 4): name, latitude/longitude (pasting
 * "lat, lng" fills both) and an optional check-in range. Without a range the
 * organization default applies, as the backend does today.
 */
export const NewQontactPointModal: React.FC<NewQontactPointModalProps> = ({
    isOpen,
    orgID,
    onClose,
    onCreated,
}) => {
    const [name, setName] = useState("");
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [triggerDistance, setTriggerDistance] = useState("");
    const [coordinatesWereSplit, setCoordinatesWereSplit] = useState(false);
    const [errors, setErrors] = useState(emptyErrors);
    const [isCreating, setIsCreating] = useState(false);
    const [isHelpVisible, setIsHelpVisible] = useState(false);
    // Organization default range, shown prefilled in the field
    const [defaultTriggerDistance, setDefaultTriggerDistance] = useState<number | undefined>(undefined);

    // Start clean every time the modal opens
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setName("");
        setLatitude("");
        setLongitude("");
        setTriggerDistance("");
        setCoordinatesWereSplit(false);
        setErrors(emptyErrors);
        setIsHelpVisible(false);
        setDefaultTriggerDistance(undefined);

        let isCancelled = false;
        Application.shared().coreDependencies.qontactPointsService.getDefaultTriggerDistance(orgID)
            .then((value) => {
                if (isCancelled || value === undefined) {
                    return;
                }
                setDefaultTriggerDistance(value);
                // Don't overwrite what the user already typed
                setTriggerDistance((current) => current === "" ? String(value) : current);
            })
            .catch((error) => console.error(error));
        return () => {
            isCancelled = true;
        };
    }, [isOpen, orgID]);

    // Map preview: marker and check-in range for valid coordinates
    const mapPoint = useMemo(() => {
        const lat = Number(latitude);
        const lng = Number(longitude);
        const isValid = latitude.trim() !== "" && longitude.trim() !== "" &&
            Number.isFinite(lat) && Math.abs(lat) <= 90 && Number.isFinite(lng) && Math.abs(lng) <= 180;
        return isValid ? { lat, lng } : null;
    }, [latitude, longitude]);

    const mapMarkers = useMemo(() => mapPoint
        ? [{ id: "new-qontact-point", status: "GREEN", latitude: mapPoint.lat, longitude: mapPoint.lng, title: name.trim() || "Nuevo Qontact Point".localized() }]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapPoint]);

    const rangeInMeters = Number(triggerDistance) > 0 ? Number(triggerDistance) : (defaultTriggerDistance ?? 0);
    const mapCircles = useMemo(() => mapPoint && rangeInMeters > 0
        ? [{
            id: "new-qontact-point-range",
            center: { latitude: mapPoint.lat, longitude: mapPoint.lng },
            radiusInMeters: rangeInMeters,
            strokeColor: "#EC6C56",
            fillColor: "#EC6C56",
        }]
        : [],
    [mapPoint, rangeInMeters]);

    /** Click on the map: puts the point there (6 decimals) */
    const handleMapClick = (selectedLatitude: number, selectedLongitude: number) => {
        if (isCreating) return;
        setLatitude(selectedLatitude.toFixed(6));
        setLongitude(selectedLongitude.toFixed(6));
        setCoordinatesWereSplit(false);
        setErrors((current) => ({ ...current, latitude: "", longitude: "" }));
    };

    const handleCreate = async () => {
        if (isCreating) {
            return;
        }

        const distance = triggerDistance.trim();
        const nextErrors = {
            name: name.trim() ? "" : "%s es obligatorio".localized("Nombre".localized()),
            latitude: validateLatitude(latitude),
            longitude: validateLongitude(longitude),
            triggerDistance:
                distance && (!Number.isInteger(Number(distance)) || Number(distance) <= 0 || Number(distance) > maxTriggerDistanceInMeters)
                    ? "Ingresá un número de metros entre 1 y %s".localized(String(maxTriggerDistanceInMeters))
                    : "",
        };
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            return;
        }

        setIsCreating(true);
        try {
            await Application.shared().coreDependencies.qontactPointsService.createQontactPoint(orgID, {
                name: name.trim(),
                latitude: Number(latitude),
                longitude: Number(longitude),
                // Only send the range when it differs from the organization default, so the
                // new qontact point keeps following the organization value (as today)
                ...(distance && Number(distance) !== defaultTriggerDistance ? { triggerDistance: Number(distance) } : {}),
            });
            ToastManager.shared().showSuccessMessage("Qontact Point creado".localized());
            onCreated();
            onClose();
        } catch (error) {
            ToastManager.shared().showErrorMessage("No se pudo crear el Qontact Point".localized());
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={isCreating ? () => {} : onClose}
            title={"Nuevo Qontact Point".localized()}
            width="720px"
            maxWidth="94vw"
        >
            <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                <TextInput
                    label={"Nombre".localized()}
                    value={name}
                    placeholder={"ej. garita norte".localized()}
                    error={errors.name}
                    disabled={isCreating}
                    onChange={(value) => {
                        setName(value);
                        setErrors((current) => ({ ...current, name: "" }));
                    }}
                    onEnterPress={handleCreate}
                />
                <CoordinatesFields
                    latitude={latitude}
                    longitude={longitude}
                    latitudeError={errors.latitude}
                    longitudeError={errors.longitude}
                    wasSplit={coordinatesWereSplit}
                    disabled={isCreating}
                    onLatitudeChange={(value) => {
                        setLatitude(value);
                        setCoordinatesWereSplit(false);
                        setErrors((current) => ({ ...current, latitude: "" }));
                    }}
                    onLongitudeChange={(value) => {
                        setLongitude(value);
                        setCoordinatesWereSplit(false);
                        setErrors((current) => ({ ...current, longitude: "" }));
                    }}
                    onPairPasted={(pastedLatitude, pastedLongitude) => {
                        setLatitude(pastedLatitude);
                        setLongitude(pastedLongitude);
                        setCoordinatesWereSplit(true);
                        setErrors((current) => ({ ...current, latitude: "", longitude: "" }));
                    }}
                    onEnterPress={handleCreate}
                />
                <TextInput
                    label={"Rango de fichada (metros)".localized()}
                    value={triggerDistance}
                    placeholder={defaultTriggerDistance !== undefined
                        ? String(defaultTriggerDistance)
                        : "Por defecto de tu organización".localized()}
                    inputMode="numeric"
                    error={errors.triggerDistance}
                    disabled={isCreating}
                    onChange={(value) => {
                        setTriggerDistance(value.replace(/\D/g, ""));
                        setErrors((current) => ({ ...current, triggerDistance: "" }));
                    }}
                    onEnterPress={handleCreate}
                />
                {/* Map preview: appears once there are valid coordinates (e.g. after pasting them) */}
                {isOpen && mapPoint && (
                    <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                        <div style={{ width: "100%", height: "240px" }}>
                            <Map
                                markers={mapMarkers}
                                circles={mapCircles}
                                radius={designSystemStyles.cornerRadiusComponent}
                                setMapRefParent={() => {}}
                                onMapClick={handleMapClick}
                            />
                        </div>
                        <SmallText
                            text={"Tocá el mapa si querés ajustar el punto.".localized()}
                            color={designSystemStyles.colorTextSecondary}
                        />
                    </VerticalContainer>
                )}
                <SimpleCardContainer variant="inlineSetup">
                <HorizontalContainer width="100%" alignment="flex-start" gap={designSystemStyles.gapSm}>
                <SystemIcon type="info" size="small" color={designSystemStyles.colorTextSecondary} />
                <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm} margin="0" alignment="flex-start">
                    <SmallText
                        text={(defaultTriggerDistance !== undefined
                            ? "Pegá «latitud, longitud» de Google Maps y se completan solos. El rango por defecto es de %s metros."
                            : "Pegá «latitud, longitud» de Google Maps y se completan solos.").localized(String(defaultTriggerDistance ?? ""))}
                        color={designSystemStyles.colorTextSecondary}
                    />
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsHelpVisible((visible) => !visible)}
                        onKeyDown={(event) => { if (event.key === "Enter") setIsHelpVisible((visible) => !visible); }}
                        style={{ cursor: "pointer", width: "fit-content" }}
                    >
                        <SmallText
                            text={`**${"¿Cómo copiar coordenadas desde Google Maps?".localized()}**`}
                            markdown={true}
                            color={designSystemStyles.colorMainAction}
                        />
                    </span>
                    {isHelpVisible && <CoordinatesHelpVideo embedded={true} />}
                </VerticalContainer>
                </HorizontalContainer>
                </SimpleCardContainer>
                {/* Fixed footer: the map makes the content scroll */}
                <StickyModalFooter>
                <HorizontalContainer width="100%" justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                    <HollowedButton
                        text={"Cancelar".localized()}
                        onClick={onClose}
                        width="160px"
                        disabled={isCreating}
                    />
                    <NormalButton
                        text={"Crear Qontact Point".localized()}
                        onClick={handleCreate}
                        width="200px"
                        loading={isCreating}
                        disabled={isCreating}
                    />
                </HorizontalContainer>
                </StickyModalFooter>
            </VerticalContainer>
        </Modal>
    );
};

export default NewQontactPointModal;
